import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { toast } from "sonner";
import {
  Landmark, Upload, Loader2, Check, X, Trash2, RefreshCw,
  Link2, KeyRound, FileText, Sparkles, ArrowDownCircle, ArrowUpCircle,
} from "lucide-react";

const fmt = (n) => (n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function BankTab({ api }) {
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [alertCfg, setAlertCfg] = useState({ enabled: true, seuil: 500 });
  const [alertSaving, setAlertSaving] = useState(false);
  // GoCardless
  const [gcConfig, setGcConfig] = useState(null);
  const [institutions, setInstitutions] = useState([]);
  const [selectedBank, setSelectedBank] = useState("");
  const [gcAccounts, setGcAccounts] = useState([]);
  const [gcBusy, setGcBusy] = useState(false);
  const fileRef = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const [s, t, g, a] = await Promise.allSettled([
        api.get("/tresorerie/bank/summary"),
        api.get("/tresorerie/bank/transactions?limit=100"),
        api.get("/tresorerie/bank/gocardless/config"),
        api.get("/tresorerie/bank/gocardless/accounts"),
      ]);
      if (s.status === "fulfilled") {
        setSummary(s.value.data);
        if (s.value.data?.alert_config) setAlertCfg(s.value.data.alert_config);
      }
      if (t.status === "fulfilled") setTransactions(t.value.data || []);
      if (g.status === "fulfilled") setGcConfig(g.value.data);
      if (a.status === "fulfilled") setGcAccounts(a.value.data || []);
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("gc_ref");
    if (ref) {
      api.post("/tresorerie/bank/gocardless/finalize", { reference: ref })
        .then(r => { toast.success(`${r.data.accounts_linked} compte(s) bancaire(s) connecté(s)`); load(); })
        .catch(e => toast.error(e?.response?.data?.detail || "Finalisation impossible"));
      window.history.replaceState({}, "", window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/tresorerie/bank/import-ai", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Extraction impossible");
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const confirmImport = async () => {
    setConfirming(true);
    try {
      const r = await api.post("/tresorerie/bank/import-confirm", {
        transactions: preview.transactions,
        file_name: preview.file_name,
        bank_name: preview.bank_name,
        account_label: preview.account_label,
      });
      toast.success(`${r.data.imported} transaction(s) importée(s), ${r.data.matched} rapprochée(s)${r.data.duplicates ? `, ${r.data.duplicates} doublon(s) ignoré(s)` : ""}`);
      setPreview(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import impossible");
    }
    setConfirming(false);
  };

  const updatePreviewTx = (idx, field, value) => {
    setPreview(p => {
      const txs = [...p.transactions];
      txs[idx] = { ...txs[idx], [field]: field === "amount" ? parseFloat(value) || 0 : value };
      return { ...p, transactions: txs };
    });
  };

  const removePreviewTx = (idx) => setPreview(p => ({ ...p, transactions: p.transactions.filter((_, i) => i !== idx) }));

  const loadInstitutions = async () => {
    setGcBusy(true);
    try {
      const r = await api.get("/tresorerie/bank/gocardless/institutions?country=FR");
      setInstitutions(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de lister les banques");
    }
    setGcBusy(false);
  };

  const connectBank = async () => {
    if (!selectedBank) return;
    setGcBusy(true);
    try {
      const r = await api.post("/tresorerie/bank/gocardless/connect", {
        institution_id: selectedBank,
        redirect_base: window.location.origin,
      });
      window.location.href = r.data.link;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Connexion impossible");
      setGcBusy(false);
    }
  };

  const syncAccount = async (accountId) => {
    setGcBusy(true);
    try {
      const r = await api.post("/tresorerie/bank/gocardless/sync", { account_id: accountId });
      toast.success(`${r.data.imported} transaction(s) synchronisée(s), ${r.data.matched} rapprochée(s)`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Synchro impossible");
    }
    setGcBusy(false);
  };

  const deleteTx = async (txId) => {
    try {
      await api.delete(`/tresorerie/bank/transactions/${txId}`);
      setTransactions(txs => txs.filter(t => t.tx_id !== txId));
    } catch {}
  };

  if (loading) return <div className="text-center py-12" style={{ color: "var(--text-muted)" }}>Chargement...</div>;

  return (
    <div className="space-y-5" data-testid="bank-tab">
      {/* KPIs banque */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { id: "solde-net", label: "Solde net (importé)", value: `${fmt(summary.solde_net_banque)} €`, color: summary.solde_net_banque >= 0 ? "#22c55e" : "#ef4444" },
            { id: "credits", label: "Crédits ce mois", value: `+${fmt(summary.credits_mois)} €`, color: "#22c55e" },
            { id: "debits", label: "Débits ce mois", value: `${fmt(summary.debits_mois)} €`, color: "#ef4444" },
            { id: "rapprochees", label: "Rapprochées", value: `${summary.matched_count}/${summary.transactions_count}`, color: "#f7c935" },
          ].map(k => (
            <div key={k.label} className="rounded-xl p-4 border" style={{ background: "var(--card-bg, #14101c)", borderColor: "rgba(255,255,255,0.06)" }} data-testid={`bank-kpi-${k.id}`}>
              <div className="text-[10px] uppercase font-bold tracking-wide mb-1" style={{ color: "var(--text-muted)" }}>{k.label}</div>
              <div className="text-xl font-black" style={{ color: k.color }}>{k.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Alerte écart théorique vs réel */}
      <div className="rounded-xl p-4 border flex items-center justify-between flex-wrap gap-3" style={{ background: "var(--card-bg, #14101c)", borderColor: "rgba(255,255,255,0.06)" }} data-testid="ecart-alert-card">
        <div className="flex items-center gap-3">
          <label className="relative inline-flex items-center cursor-pointer" data-testid="ecart-alert-toggle">
            <input type="checkbox" checked={alertCfg.enabled} onChange={e => setAlertCfg(c => ({ ...c, enabled: e.target.checked }))} className="sr-only peer" />
            <div className="w-9 h-5 rounded-full bg-white/10 peer-checked:bg-[#f7c935] transition-colors relative after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-4 after:h-4 after:rounded-full after:bg-white after:transition-transform peer-checked:after:translate-x-4" />
          </label>
          <div>
            <div className="text-sm font-bold" style={{ color: "var(--text-main, #fff)" }}>Alerte écart théorique vs réel</div>
            <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>
              Déclenche une alerte trésorerie + notification quand l'écart entre le solde bancaire importé et le solde net théorique dépasse le seuil.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] uppercase font-bold" style={{ color: "var(--text-muted)" }}>Seuil</span>
            <input type="number" min="0" step="50" value={alertCfg.seuil}
              onChange={e => setAlertCfg(c => ({ ...c, seuil: e.target.value }))}
              className="w-24 rounded-lg px-2 py-1.5 text-sm border bg-transparent text-right font-bold"
              style={{ borderColor: "rgba(255,255,255,0.1)", color: "var(--text-main, #fff)" }}
              data-testid="ecart-seuil-input" />
            <span className="text-xs font-bold" style={{ color: "var(--text-muted)" }}>€</span>
          </div>
          <Button size="sm" disabled={alertSaving}
            onClick={async () => {
              setAlertSaving(true);
              try {
                const r = await api.put("/tresorerie/bank/alert-config", { enabled: alertCfg.enabled, seuil: parseFloat(alertCfg.seuil) || 0 });
                setAlertCfg({ enabled: r.data.enabled, seuil: r.data.seuil });
                toast.success(`Alerte ${r.data.enabled ? "activée" : "désactivée"} — seuil ${r.data.seuil} €`);
              } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
              setAlertSaving(false);
            }}
            className="bg-[#f7c935]/10 text-[#f7c935] hover:bg-[#f7c935]/20 border border-[#f7c935]/30 h-8"
            data-testid="ecart-alert-save-btn">
            {alertSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />} Enregistrer
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Import fichier IA */}
        <div className="rounded-xl p-5 border" style={{ background: "var(--card-bg, #14101c)", borderColor: "rgba(255,255,255,0.06)" }} data-testid="bank-file-import-card">
          <h3 className="text-sm font-bold flex items-center gap-2 mb-2" style={{ color: "var(--text-main, #fff)" }}>
            <FileText className="w-4 h-4 text-[#f7c935]" /> Import relevé PDF / CSV (OCR IA)
          </h3>
          <p className="text-[11px] mb-3" style={{ color: "var(--text-muted)" }}>
            Déposez un relevé bancaire : l'IA extrait chaque transaction avec précision, vous vérifiez avant import, puis rapprochement automatique avec vos encaissements et dépenses.
          </p>
          <input ref={fileRef} type="file" accept=".pdf,.csv,.txt" className="hidden" data-testid="bank-file-input"
            onChange={e => uploadFile(e.target.files?.[0])} />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="w-full bg-[#f7c935]/10 text-[#f7c935] hover:bg-[#f7c935]/20 border border-[#f7c935]/30" data-testid="bank-upload-btn">
            {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyse IA en cours...</> : <><Upload className="w-4 h-4 mr-2" /> Choisir un relevé (PDF ou CSV)</>}
          </Button>
        </div>

        {/* GoCardless */}
        <div className="rounded-xl p-5 border" style={{ background: "var(--card-bg, #14101c)", borderColor: "rgba(255,255,255,0.06)" }} data-testid="gocardless-card">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: "var(--text-main, #fff)" }}>
              <Landmark className="w-4 h-4 text-[#3b82f6]" /> Connexion bancaire (API PSD2)
            </h3>
            {gcConfig?.configured && (
              <span className="text-[9px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 font-bold" data-testid="gc-configured-badge">Clés OK · {gcConfig.secret_id_masked}</span>
            )}
          </div>
          <p className="text-[11px] mb-3" style={{ color: "var(--text-muted)" }}>
            GoCardless Bank Account Data (gratuit, 2400+ banques EU). Créez vos clés sur <span className="text-[#3b82f6]">bankaccountdata.gocardless.com</span> → User Secrets.
          </p>

          {!gcConfig?.configured ? (
            <div className="space-y-2" data-testid="gc-not-configured">
              <p className="text-[11px]" style={{ color: "var(--text-muted)" }}>
                Les clés GoCardless se configurent désormais dans le Hub API (une seule page pour toutes les intégrations).
              </p>
              <Button onClick={() => navigate("/admin/integrations-health")} size="sm" className="bg-[#3b82f6] text-white hover:bg-[#2563eb]" data-testid="gc-goto-hub-btn">
                <KeyRound className="w-4 h-4 mr-1" /> Configurer dans le Hub API
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex gap-2">
                {institutions.length === 0 ? (
                  <Button onClick={loadInstitutions} disabled={gcBusy} size="sm" className="bg-[#3b82f6]/10 text-[#3b82f6] hover:bg-[#3b82f6]/20 border border-[#3b82f6]/30 flex-1" data-testid="gc-list-banks-btn">
                    {gcBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Landmark className="w-4 h-4 mr-1" />} Choisir ma banque
                  </Button>
                ) : (
                  <>
                    <select value={selectedBank} onChange={e => setSelectedBank(e.target.value)}
                      className="flex-1 rounded-lg px-3 py-2 text-sm border bg-[#14101c]" style={{ borderColor: "rgba(255,255,255,0.1)", color: "var(--text-main, #fff)" }} data-testid="gc-bank-select">
                      <option value="">Sélectionner une banque ({institutions.length})...</option>
                      {institutions.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                    </select>
                    <Button onClick={connectBank} disabled={!selectedBank || gcBusy} size="sm" className="bg-[#3b82f6] text-white hover:bg-[#2563eb]" data-testid="gc-connect-btn">
                      <Link2 className="w-4 h-4 mr-1" /> Connecter
                    </Button>
                  </>
                )}
                <button onClick={() => navigate("/admin/integrations-health")} className="text-[10px] px-2 rounded hover:bg-white/5" style={{ color: "var(--text-muted)" }} title="Modifier les clés dans le Hub API" data-testid="gc-edit-keys-btn">
                  <KeyRound className="w-3.5 h-3.5" />
                </button>
              </div>
              {gcAccounts.length > 0 && (
                <div className="space-y-1.5" data-testid="gc-accounts-list">
                  {gcAccounts.map(a => (
                    <div key={a.account_id} className="flex items-center gap-2 p-2 rounded-lg border" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                      <Landmark className="w-3.5 h-3.5 text-[#3b82f6] flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-bold truncate" style={{ color: "var(--text-main, #fff)" }}>{a.name}</div>
                        <div className="text-[10px]" style={{ color: "var(--text-muted)" }}>{a.iban || a.account_id.slice(0, 18)}{a.last_sync_at ? ` · sync ${a.last_sync_at.slice(0, 10)}` : ""}</div>
                      </div>
                      <Button onClick={() => syncAccount(a.account_id)} disabled={gcBusy} size="sm" className="h-7 text-[10px] bg-[#3b82f6]/10 text-[#3b82f6] hover:bg-[#3b82f6]/20" data-testid={`gc-sync-${a.account_id}`}>
                        <RefreshCw className="w-3 h-3 mr-1" /> Synchroniser
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Transactions list */}
      <div className="rounded-xl border overflow-hidden" style={{ background: "var(--card-bg, #14101c)", borderColor: "rgba(255,255,255,0.06)" }}>
        <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
          <h3 className="text-sm font-bold" style={{ color: "var(--text-main, #fff)" }}>Transactions bancaires ({transactions.length})</h3>
          <Button onClick={async () => { const r = await api.post("/tresorerie/bank/match-auto"); toast.success(`${r.data.matched} transaction(s) rapprochée(s)`); load(); }}
            size="sm" variant="outline" className="border-white/10 text-xs h-7" data-testid="bank-match-auto-btn">
            <Check className="w-3.5 h-3.5 mr-1" /> Rapprocher auto
          </Button>
        </div>
        {transactions.length === 0 ? (
          <div className="text-center py-10 text-sm" style={{ color: "var(--text-muted)" }} data-testid="bank-empty-state">
            Aucune transaction — importez un relevé ou connectez votre banque.
          </div>
        ) : (
          <div className="max-h-[420px] overflow-y-auto">
            <table className="w-full text-left">
              <thead className="sticky top-0" style={{ background: "var(--card-bg, #14101c)" }}>
                <tr className="border-b" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                  {["Date", "Libellé", "Montant", "Source", "Rapprochement", ""].map(h => (
                    <th key={h} className="px-4 py-2 text-[10px] uppercase font-bold" style={{ color: "var(--text-muted)" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {transactions.map(t => (
                  <tr key={t.tx_id} className="border-b hover:bg-white/[0.02]" style={{ borderColor: "rgba(255,255,255,0.03)" }} data-testid={`bank-tx-${t.tx_id}`}>
                    <td className="px-4 py-2 text-[11px] font-mono" style={{ color: "var(--text-muted)" }}>{t.date}</td>
                    <td className="px-4 py-2 text-xs max-w-xs truncate" style={{ color: "var(--text-main, #fff)" }} title={t.label}>{t.label}</td>
                    <td className={`px-4 py-2 text-xs font-bold ${t.amount >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                      <span className="inline-flex items-center gap-1">
                        {t.amount >= 0 ? <ArrowUpCircle className="w-3 h-3" /> : <ArrowDownCircle className="w-3 h-3" />}
                        {fmt(t.amount)} €
                      </span>
                    </td>
                    <td className="px-4 py-2"><span className="text-[9px] px-1.5 py-0.5 rounded bg-white/5" style={{ color: "var(--text-muted)" }}>{t.source === "gocardless" ? "API banque" : "Fichier"}</span></td>
                    <td className="px-4 py-2">
                      {t.status === "matched" ? (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 font-bold">✓ {t.matched_type === "encaissement" ? "Encaissement" : "Dépense"}</span>
                      ) : (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-300">Non rapprochée</span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <button onClick={() => deleteTx(t.tx_id)} className="p-1 rounded hover:bg-red-500/10" title="Supprimer" data-testid={`bank-tx-delete-${t.tx_id}`}>
                        <Trash2 className="w-3.5 h-3.5 text-red-400/60" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal preview OCR */}
      {preview && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="bank-preview-modal">
          <div className="rounded-xl border w-full max-w-3xl max-h-[88vh] flex flex-col" style={{ background: "#0b0811", borderColor: "rgba(255,255,255,0.1)" }}>
            <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
              <div>
                <h3 className="text-base font-bold text-white flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#f7c935]" /> Vérification avant import</h3>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  {preview.file_name}{preview.bank_name ? ` · ${preview.bank_name}` : ""}{preview.period ? ` · ${preview.period}` : ""} — {preview.transactions.length} transaction(s) détectée(s). Corrigez ou supprimez si besoin.
                </p>
              </div>
              <button onClick={() => setPreview(null)} className="p-1 rounded hover:bg-white/10" data-testid="bank-preview-close"><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-3">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="py-2 text-[10px] uppercase font-bold text-slate-500 w-32">Date</th>
                    <th className="py-2 text-[10px] uppercase font-bold text-slate-500">Libellé</th>
                    <th className="py-2 text-[10px] uppercase font-bold text-slate-500 w-32">Montant (€)</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {preview.transactions.map((t, idx) => (
                    <tr key={idx} className="border-b border-white/5" data-testid={`preview-tx-${idx}`}>
                      <td className="py-1.5 pr-2">
                        <input value={t.date} onChange={e => updatePreviewTx(idx, "date", e.target.value)}
                          className="w-full bg-[#161020] border border-white/10 rounded px-2 py-1 text-[11px] text-white font-mono" />
                      </td>
                      <td className="py-1.5 pr-2">
                        <input value={t.label} onChange={e => updatePreviewTx(idx, "label", e.target.value)}
                          className="w-full bg-[#161020] border border-white/10 rounded px-2 py-1 text-[11px] text-white" />
                      </td>
                      <td className="py-1.5 pr-2">
                        <input type="number" step="0.01" value={t.amount} onChange={e => updatePreviewTx(idx, "amount", e.target.value)}
                          className={`w-full bg-[#161020] border border-white/10 rounded px-2 py-1 text-[11px] font-bold ${t.amount >= 0 ? "text-emerald-400" : "text-red-400"}`} />
                      </td>
                      <td className="py-1.5">
                        <button onClick={() => removePreviewTx(idx)} className="p-1 rounded hover:bg-red-500/10"><X className="w-3.5 h-3.5 text-red-400/70" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-5 py-4 border-t flex items-center justify-between" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
              <div className="text-xs text-slate-400">
                Total : <span className="text-emerald-400 font-bold">+{fmt(preview.transactions.filter(t => t.amount > 0).reduce((s, t) => s + t.amount, 0))} €</span>
                {" / "}
                <span className="text-red-400 font-bold">{fmt(preview.transactions.filter(t => t.amount < 0).reduce((s, t) => s + t.amount, 0))} €</span>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => setPreview(null)} variant="outline" className="border-white/10 text-slate-300" data-testid="bank-preview-cancel">Annuler</Button>
                <Button onClick={confirmImport} disabled={confirming || preview.transactions.length === 0} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold" data-testid="bank-preview-confirm">
                  {confirming ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />} Importer {preview.transactions.length} transaction(s)
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
